import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { shallow } from 'zustand/shallow';

import useUIStore from '../store/ui';

import { isWoodyError } from '@fabric-app/business/errors/errors.utils';
import { pick } from '@fabric-app/utils/object/manipulation';
import { useQueryResourceDetail } from '@fabric-app/business/resources/queries/useQueryResourceDetail';
import ExpandedFdoc from '@fabric-app/business/resource-detail/components/ExpandedFdoc/ExpandedFdoc';
import { ResourceDataContextProvider } from '@fabric-app/business/resource-detail/components/context/ResourceDataContextProvider';
import {
  useExpandedResource,
  useShowExpandedResource,
  useSyncExpandedResourceIdWithRouter,
} from '@fabric-app/business/resources/stores/useExpandedResourceStore';
import type { ParsedUrlQuery } from 'querystring';
import { isNumber } from '@fabric-app/utils/guards';
import { useResourceSwitcher } from '@fabric-app/business/resource-detail/contexts/resourcesSwitcher';

const OVERSCAN_FETCH_NEXT_PAGE = 3;

const useExpandedFdocElement = () => {
  const router = useRouter();
  const { isNewModalOpen } = useUIStore((state) => pick(state, ['isNewModalOpen']), shallow);

  const expandedResourceId = useExpandedResource();
  const showExpandedResource = useShowExpandedResource();

  const routerQueryRef = useRef<ParsedUrlQuery>(router.query);
  routerQueryRef.current = router.query;

  useSyncExpandedResourceIdWithRouter();

  const onCloseExpandedFdoc = useCallback(() => {
    showExpandedResource(null);
  }, [showExpandedResource]);

  const { resource, error } = useQueryResourceDetail(
    expandedResourceId
      ? {
          resourceId: expandedResourceId,
          includeHidden: true,
        }
      : undefined,
    {
      placeholderData: undefined,
    },
  );

  useEffect(() => {
    if (
      !error ||
      !isWoodyError(error) ||
      ![401, 403, 404].includes(error.status) ||
      !expandedResourceId
    )
      return;

    onCloseExpandedFdoc();
  }, [error, onCloseExpandedFdoc, expandedResourceId]);

  const { resourceIds: switcherResourceIds, fetchNextPage } = useResourceSwitcher();

  const expandedResourceIndex = useMemo(
    () => (expandedResourceId ? switcherResourceIds.indexOf(expandedResourceId) : null),
    [switcherResourceIds, expandedResourceId],
  );

  /**
   * fetch next page of items when the user is close to the end of the list
   */
  useEffect(() => {
    if (
      isNumber(expandedResourceIndex) &&
      expandedResourceIndex > -1 &&
      expandedResourceIndex + OVERSCAN_FETCH_NEXT_PAGE > switcherResourceIds.length
    ) {
      fetchNextPage?.();
    }
  }, [expandedResourceIndex, fetchNextPage, switcherResourceIds]);

  const { prevResourceId, nextResourceId } = useMemo(() => {
    if (isNumber(expandedResourceIndex) && expandedResourceIndex !== -1) {
      return {
        prevResourceId: switcherResourceIds[expandedResourceIndex - 1],
        nextResourceId: switcherResourceIds[expandedResourceIndex + 1],
      };
    }
    return {
      prevResourceId: undefined,
      nextResourceId: undefined,
    };
  }, [expandedResourceIndex, switcherResourceIds]);

  /**
   * data prefetching
   */

  useQueryResourceDetail(
    nextResourceId
      ? {
          resourceId: nextResourceId,
          includeHidden: true,
        }
      : undefined,
    {
      refetchInterval: 45000,
    },
  );

  useQueryResourceDetail(
    prevResourceId
      ? {
          resourceId: prevResourceId,
          includeHidden: true,
        }
      : undefined,
    {
      refetchInterval: 45000,
    },
  );

  /**
   * navigation handlers
   */

  const goToNextResource = useCallback(() => {
    if (!nextResourceId) return;
    showExpandedResource(nextResourceId, { preserveCurrentTab: true });
  }, [nextResourceId, showExpandedResource]);

  const goToPrevResource = useCallback(() => {
    if (!prevResourceId) return;
    showExpandedResource(prevResourceId, { preserveCurrentTab: true });
  }, [prevResourceId, showExpandedResource]);

  const component = useMemo(() => {
    return expandedResourceId && !isNewModalOpen ? (
      <ResourceDataContextProvider resource={resource}>
        <ExpandedFdoc
          resourceId={expandedResourceId}
          onClose={onCloseExpandedFdoc}
          key={`expanded-fdoc-${expandedResourceId}`}
          goToNextResource={goToNextResource}
          goToPrevResource={goToPrevResource}
        />
      </ResourceDataContextProvider>
    ) : null;
  }, [
    expandedResourceId,
    isNewModalOpen,
    onCloseExpandedFdoc,
    resource,
    goToPrevResource,
    goToNextResource,
  ]);

  return {
    expandedFdoc: component,
    expandedFdocId: expandedResourceId,
  };
};

/**
 * @deprecated
 * use ExpandedResource, which currently supports mobile only, in the future
 * We're wrapping useExpandedFdocElement with a component so we can conditionally render it (hooks can't be conditionally rendered)
 */
export const ExpandedFdoc_OLD = () => {
  const { expandedFdoc } = useExpandedFdocElement();
  return expandedFdoc;
};
